import React, {ReactNode, useState, useRef} from 'react'

import {Alert, IconButton, P} from '../index.js'
import {FieldProps} from './types.js'
import ControlLabel from './ControlLabel.js'
import {field, fieldMargins} from './styles.js'

// readOnly hides the pick/remove actions but still shows the current image
export interface UploadImageProps extends FieldProps {
    subText?: ReactNode
    image?: string
    // Called with the image as a data URL, or with '' when the image is removed
    onChange?: (value: string) => void
    maxWidth?: number
    maxHeight?: number
    minWidth?: number
    minHeight?: number
    showDimensionsError?: boolean
    resize?: boolean
    maxSize?: number // in MB
    // MIME types for the file input, e.g. 'image/png,image/svg+xml'
    accept?: string
}

const UploadImage: React.FC<UploadImageProps> = (props) => {
    const {label, subText, info, image, accept, disabled, readOnly, style} = props

    const [error, setError] = useState('')

    const fileControlRef = useRef<HTMLInputElement | null>(null)

    const upload = (event: React.MouseEvent) => {
        event.preventDefault()
        if (disabled || readOnly) return
        setError('')
        fileControlRef.current.click()
    }

    const saveImage = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault()
        const {maxWidth, maxHeight, minHeight, minWidth, showDimensionsError, resize = true, maxSize} = props

        const file = event.target.files?.[0]

        // Reset so picking the same file again (e.g. after removing it) fires change again
        event.target.value = ''

        if (!file) return

        if (maxSize && file.size) {
            const sizeInMB = file.size / 1024 / 1024

            if (sizeInMB > maxSize) {
                setError(`Afbeelding is te groot. Maximale grootte is ${maxSize}MB.`)
                return
            }
        }

        const reader = new FileReader()
        const img = document.createElement('img')

        reader.onerror = () => setError('Afbeelding kon niet worden gelezen.')

        reader.onloadend = (event) => {
            if (reader.error) return

            img.onerror = () => setError('Bestand is geen geldige afbeelding.')

            img.onload = () => {
                if (showDimensionsError) {
                    if (!(img.width <= maxWidth && img.width >= minWidth && img.height <= maxHeight && img.height >= minHeight)) {
                        setError(`Afbeelding is niet het gewenste formaat. Minimale hoogte: ${minHeight} pixels, maximale hoogte: ${maxHeight} pixels, minimale breedte: ${minWidth} pixels, maximale breedte: ${maxWidth} pixels.`)
                        return
                    }
                }
                const MAX_WIDTH = maxWidth || 256
                const MAX_HEIGHT = maxHeight || 128

                if (!resize) {
                    props.onChange?.(event.target.result.toString())
                    return
                }

                if (img.width <= MAX_WIDTH && img.height <= MAX_HEIGHT) {
                    props.onChange?.(event.target.result.toString())
                } else {
                    // Scale down so both dimensions fit, keeping the aspect ratio
                    const scale = Math.min(MAX_WIDTH / img.width, MAX_HEIGHT / img.height, 1)
                    const width = img.width * scale
                    const height = img.height * scale

                    const canvas = document.createElement('canvas')
                    canvas.width = width
                    canvas.height = height
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height)

                    const dataurl = canvas.toDataURL('image/png')
                    props.onChange?.(dataurl)
                }
            }

            img.src = event.target.result.toString()
        }

        reader.readAsDataURL(file)
    }

    const removeImage = (event: React.MouseEvent) => {
        event.preventDefault()
        if (disabled || readOnly) return
        setError('')
        props.onChange?.('')
    }

    const defaultStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 'auto',
        minHeight: field.height,
        ...fieldMargins,
        ...style
    }

    return (
        <>
            <div style={defaultStyle}>
                <div>
                    {label &&
                        <ControlLabel info={info} disabled={disabled}>
                            <div style={{whiteSpace: 'nowrap'}}>{label}</div>
                        </ControlLabel>
                    }

                    {subText &&
                        <P disabled={disabled}>
                            {subText}
                        </P>
                    }
                </div>

                <div style={{display: 'flex', alignItems: 'center'}}>

                    {image &&
                        <img
                            id='image'
                            src={image}
                            style={{maxWidth: 128, maxHeight: 64, marginRight: 12, opacity: disabled ? 0.5 : 1}}
                        />
                    }

                    {!readOnly &&
                        <IconButton disabled={disabled} aria-label='Afbeelding kiezen' onClick={upload}>
                            <i className={image ? 'mdi mdi-pencil' : 'mdi mdi-upload'} title='Afbeelding kiezen' />
                        </IconButton>
                    }

                    {image && !readOnly &&
                        <IconButton disabled={disabled} aria-label='Verwijderen' onClick={removeImage}>
                            <i className='mdi mdi-delete' title='Verwijderen' />
                        </IconButton>
                    }
                </div>
                <input
                    style={{display: 'none'}}
                    type='file'
                    accept={accept || 'image/png'}
                    disabled={disabled || readOnly}
                    ref={fileControlRef}
                    onChange={saveImage}
                />
            </div>
            {error && <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>}
        </>
    )
}

export default UploadImage
